import { Redirect, Route, Switch } from "react-router-dom";

import HandBookApproveButton from "./features/HandBookApproveButton/HandBookApproveButton";
import HandBookEditButton from "./features/HandBookEditButton/HandBookEditButton";
import HandBookEditTemplatesButton from "./features/HandBookEditButton/HandBookEditTemplatesButton";
import HandBookViewToggle from "./features/HandBookViewToggle/HandBookViewToggle";
import HandBooksTemplateExpenditures from "./tabs/HandBooksTemplates/levels/expenditures/HandBooksTemplateExpenditures";
import HandBooksTemplateSections from "./tabs/HandBooksTemplates/levels/sections/HandBooksTemplateSections";
import HandBooksTemplateSegments from "./tabs/HandBooksTemplates/levels/segments/HandBooksTemplateSegments";
import HandbookTemplates from "./tabs/HandBooksTemplates/levels/templates/HandBooksTemplates";
import { IHandbookFormField } from "./ui/HandbookForm/HandbookForm";
import { CreateHandbookProvider } from "./widgets/CreateHandbook/CreateHandbookContext";
import HandBookImport from "./widgets/HandBookImport/HandBookImport";
import HandBookList, { IHandbookColumn } from "./widgets/HandBookList/HandBookList";
import ImportTemplateDocumentation from "./widgets/ImportTemplateDocumentation";
import { useTypedSelector } from "app/store/typedUseSelector";
import CustomBreadcrumbs from "entities/CustomBreadcrumbs/CustomBreadcrumbs";
import { ALL_MEASURES, MEASURES } from "entities/MeasureSelect/MeasureSelect";
import BreadcrumbsObjectSelect from "features/templates/BreadcrumbsSelects/BreadcrumbsObjectSelect";
import TemplateBase from "features/templates/TemplateBase/TemplateBase";
import TabBar from "shared/ui/controls/TabBar/TabBar";
import ListPageHeaderTemplate from "shared/ui/layout/ListPageHeaderTemplate/ListPageHeaderTemplate";

import { HANDBOOK_EXPENDITURE_APPROVAL_STATUS } from "./model/types";
import { LOCALIZATION_CONFIG } from "constants/localization";

import { useHandbookBreadcrumbs } from "./hooks/useHandbookBreadcrumbs";
import { useHandbookSegments } from "./hooks/useHandbookSegments";
import { useCheckboxesToggleHold } from "hooks/useCheckboxesToggleHold";
import { useUrlModuleWithObjectId } from "utils/hooks/useUrlModuleWithObjectId";

import { getResourceName } from "./utils/resourceNames";
import { validateName, validatePrice, validateRequired } from "./utils/validate";
import { transformDigitToFinancial } from "utils/formatters/transformDigitToFinancial";

import styles from "./HandBook.module.scss";

//TODO filter type
const HandBook = () => {
  const routePrefix = useUrlModuleWithObjectId();
  const { title, showObjectSelect, constructLink } = useHandbookBreadcrumbs();
  const templateViewFilters = useTypedSelector((state) => state.handBookReducer.templateViewFilters);

  const tabs = [
    { text: "Работы", link: `/${routePrefix}/handbook/works` },
    { text: "Ресурсы", link: `/${routePrefix}/handbook/resources` },
    { text: "МиМ", link: `/${routePrefix}/handbook/mims` },
    { text: "Шаблоны", link: `/${routePrefix}/handbook/templates` },
  ];

  // один вызов на страницу
  useCheckboxesToggleHold();

  // Загрузка атрибутов Справочника и Шаблона. Влияет на отображение свитчера "По атрибутам" в верхнем меню
  useHandbookSegments();

  return (
    <TemplateBase
      breadcrumbs={
        <CustomBreadcrumbs title={title} subtitle="Справочники">
          {showObjectSelect && <BreadcrumbsObjectSelect constructLink={constructLink} />}
        </CustomBreadcrumbs>
      }
    >
      <CreateHandbookProvider>
        <ListPageHeaderTemplate>
          <TabBar tabs={tabs} isOnlyTabsView />
          <Switch>
            <Route
              path={[
                `/${routePrefix}/handbook/templates/:templateId/:sectionId/:subSectionId`,
                `/${routePrefix}/handbook/templates/:templateId/:sectionId`,
                `/${routePrefix}/handbook/templates/:templateId`,
                `/${routePrefix}/handbook/templates/`,
              ]}
              render={() => (
                <div className={styles.headerButtonsRight}>
                  <HandBookViewToggle />
                  <ImportTemplateDocumentation />
                  <HandBookEditTemplatesButton />
                </div>
              )}
            />
            <Route
              // Параметр :tab используется в HandBookImport
              path={[`/${routePrefix}/handbook/:tab`]}
              render={() => (
                <div className={styles.headerButtonsRight}>
                  <HandBookApproveButton />
                  <HandBookImport />
                  <HandBookEditButton />
                </div>
              )}
            />
          </Switch>
        </ListPageHeaderTemplate>
        <Switch>
          <Route
            path={`/${routePrefix}/handbook/works`}
            render={() => (
              <HandBookList
                fields={workFields}
                columns={worksCols}
                defaultFilter={{
                  type: "work",
                  status: HANDBOOK_EXPENDITURE_APPROVAL_STATUS.APPROVED,
                  ordering: "name",
                }}
                key="work"
                colPercents={defaultCols}
                formColPercents={worksFormCols}
                canAddMore
              />
            )}
          />
          <Route
            path={`/${routePrefix}/handbook/resources`}
            render={() => (
              <HandBookList
                fields={resFields}
                columns={resCols}
                defaultFilter={{
                  type: ["material", "equipment"],
                  status: HANDBOOK_EXPENDITURE_APPROVAL_STATUS.APPROVED,
                  ordering: "name",
                }}
                key="material"
                colPercents={resColsPercents}
                formColPercents={resFormCols}
                canAddMore
              />
            )}
          />
          <Route
            path={`/${routePrefix}/handbook/mims`}
            render={() => (
              <HandBookList
                fields={mimsFields}
                columns={mimsCols}
                defaultFilter={{
                  type: ["machine", "transport"],
                  status: HANDBOOK_EXPENDITURE_APPROVAL_STATUS.APPROVED,
                  ordering: "name",
                }}
                key="mim"
                colPercents={mimsColsPercents}
                formColPercents={mimsFormCols}
                canAddMore
              />
            )}
          />
          <Route
            path={`/${routePrefix}/handbook/templates/:templateId/:sectionId/:subSectionId`}
            render={() => <HandBooksTemplateExpenditures />}
            exact
          />
          <Route
            path={`/${routePrefix}/handbook/templates/:templateId/expenditures`}
            render={() => <HandBooksTemplateExpenditures />}
            exact
          />
          <Route
            exact
            path={[
              `/${routePrefix}/handbook/templates/:templateId`,
              `/${routePrefix}/handbook/templates/:templateId/:sectionId`,
            ]}
            render={() =>
              templateViewFilters.attribute ? <HandBooksTemplateSegments /> : <HandBooksTemplateSections />
            }
          />
          <Route exact path={`/${routePrefix}/handbook/templates`} render={() => <HandbookTemplates />} />
          <Redirect to={`/${routePrefix}/handbook/works`} />
        </Switch>
      </CreateHandbookProvider>
    </TemplateBase>
  );
};

export default HandBook;

export const defaultCols = [84, 9, 8];
export const worksCols: IHandbookColumn[] = [
  { header: { title: "Наименование", filterName: "name" }, render: (item) => item.name },
  { header: { title: "Ед. изм." }, render: (item) => item.measure, isCentered: true },
  {
    header: { title: `Цена ${LOCALIZATION_CONFIG.currency}` },
    render: (item) => transformDigitToFinancial(item.price || 0),
    isRighted: true,
  },
];
export const worksFormCols = [3, 79, 9, 9];
export const workFields: IHandbookFormField[] = [
  {
    label: "Наименование",
    name: "name",
    type: "text",
    validate: validateName,
  },
  { label: "Ед. изм.", name: "measure", type: "select", options: MEASURES, validate: validateRequired },
  {
    label: "Цена",
    name: "price",
    type: "text",
    validate: validatePrice,
  },
];

const resColsPercents = [59, 12, 11, 9, 9];
const resFormCols = [3, 56, 10, 13, 9, 9];
const resCols: IHandbookColumn[] = [
  {
    header: { title: "Наименование", filterName: "name" },
    render: (item) => item.name,
  },
  { header: { title: "Тип изделия", filterName: "product_type" }, render: (item) => item.product_type },

  {
    header: { title: "Категория" },
    render: (item) => getResourceName(item.type),
  },
  { header: { title: "Ед. изм." }, render: (item) => item.measure, isCentered: true },
  {
    header: { title: `Цена ${LOCALIZATION_CONFIG.currency}` },
    render: (item) => transformDigitToFinancial(item.price || 0),
    isRighted: true,
  },
];
const resFields: IHandbookFormField[] = [
  {
    label: "Наименование",
    name: "name",
    type: "text",
    validate: validateName,
  },
  { label: "Тип изделия", name: "product_type", type: "text" },
  {
    label: "Категория",
    name: "type",
    type: "select",
    options: [
      { id: "equipment", name: "Оборудование" },
      { id: "material", name: "Материал" },
    ],
    validate: validateRequired,
  },
  { label: "Ед. изм.", name: "measure", type: "select", options: MEASURES, validate: validateRequired },
  {
    label: "Цена",
    name: "price",
    type: "text",
    validate: validatePrice,
  },
];

const mimsColsPercents = [59, 12, 11, 9, 9];
const mimsFormCols = [3, 56, 10, 13, 9, 9];
const mimsCols: IHandbookColumn[] = [
  {
    header: { title: "Наименование", filterName: "name" },
    render: (item) => item.name,
  },
  { header: { title: "Тип", filterName: "product_type" }, render: (item) => item.product_type },

  {
    header: { title: "Категория" },
    render: (item) => getResourceName(item.type),
  },
  { header: { title: "Ед. изм." }, render: (item) => item.measure, isCentered: true },
  {
    header: { title: `Цена ${LOCALIZATION_CONFIG.currency}` },
    render: (item) => transformDigitToFinancial(item.price || 0),
    isRighted: true,
  },
];
const mimsFields: IHandbookFormField[] = [
  {
    label: "Наименование",
    name: "name",
    type: "text",
    validate: validateName,
  },
  { label: "Тип", name: "product_type", type: "text" },
  {
    label: "Категория",
    name: "type",
    type: "select",
    options: [
      { id: "machine", name: "МиМ" },
      { id: "transport", name: "Транспорт" },
    ],
    validate: validateRequired,
  },
  { label: "Ед. изм.", name: "measure", type: "select", options: ALL_MEASURES, validate: validateRequired },
  {
    label: "Цена",
    name: "price",
    type: "text",
    validate: validatePrice,
  },
];
